/**
 * New console — shared primitives.
 *
 * Every screen composes these rather than writing its own markup, so a change to a
 * card or a table lands everywhere at once. They are deliberately thin: a class name
 * and a couple of props, not an abstraction with opinions of its own.
 */
import {
  Children, Fragment, cloneElement, useCallback, useEffect, useLayoutEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';

import useInfiniteScroll from '@hooks/useInfiniteScroll';

import { IconChevL, IconChevR, IconInfo } from './Icons';
import { livePrefs } from '../prefs';
import { formatRange } from '../dates';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const mergeHandlers = (a, b) => (e) => {
  if (a) a(e);
  if (b) b(e);
};

const tipHost = () => (typeof document === 'undefined' ? null : (document.querySelector('.nt') || document.body));

const useTip = (a, b) => {
  const anchor = useRef(null);
  const box = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });

  useLayoutEffect(() => {
    if (!open || !anchor.current || !box.current) return undefined;
    const node = anchor.current;
    const r = (node.getBoundingClientRect().width
      ? node
      : (node.firstElementChild || node)).getBoundingClientRect();
    const t = box.current.getBoundingClientRect();
    let left = r.right + 10;
    let top = r.top + r.height / 2 - t.height / 2;
    if (left + t.width > window.innerWidth - 10) left = r.left - t.width - 10;
    top = Math.max(8, Math.min(top, window.innerHeight - t.height - 8));
    setPos((prev) => (prev.left === left && prev.top === top && prev.ready ? prev : { left, top, ready: true }));
    return undefined;
  }, [open, a, b]);

  const openedAt = useRef(0);
  const show = useCallback(() => { openedAt.current = Date.now(); setOpen(true); }, []);
  const hide = useCallback(() => { setOpen(false); setPos((p) => ({ ...p, ready: false })); }, []);

  /* A fixed-position tip left behind by a scroll points at nothing, so a scroll closes
     it — but not one that was already under way when it opened. A tap at the end of a
     flick, or the browser nudging a focused control into view, scrolls a few pixels
     after the tap, and that closed the info mark before it could be read. */
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => { if (Date.now() - openedAt.current > 300) hide(); };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, hide]);

  /* When a tip should open, and when it must not:
     - a real mouse resting on the control: yes (touch and pen fire pointerenter too,
       and on a phone that is what left every tapped control's help on screen);
     - keyboard focus: yes, but only :focus-visible — a mouse click also focuses the
       control, and that was the "helper text appears wherever I click" bug;
     - pressing the control: closes it. */
  const onPointerEnter = (e) => { if (e.pointerType === 'mouse') show(); };
  /* Mouse only. A lifted finger also fires pointerleave — after the tap that opened the
     info mark — which is what closed it the instant it appeared. On touch a tip closes
     from a tap elsewhere, a scroll, or a second tap on the same mark. */
  const onPointerLeave = (e) => { if (e.pointerType === 'mouse') hide(); };
  const onPointerDown = () => hide();
  const onFocus = (e) => {
    let keyboard = false;
    try { keyboard = e.currentTarget.matches(':focus-visible'); } catch (err) { keyboard = false; }
    if (keyboard) show();
  };
  const onBlur = () => hide();

  return {
    anchor, box, open, pos, show, hide,
    triggers: { onPointerEnter, onPointerLeave, onPointerDown, onFocus, onBlur }
  };
};

const TipPortal = ({ box, pos, heading, children }) => {
  const host = tipHost();
  if (!host) return null;
  return createPortal(
    <div
      ref={box}
      className="nt-tip"
      role="tooltip"
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
    >
      {heading ? <div className="t">{heading}</div> : null}
      {children}
    </div>,
    host
  );
};

/**
 * Hover/focus tooltip. Replaces the native `title` attribute, which arrives late,
 * looks like the OS help cursor (a question mark) and cannot carry more than a
 * sentence. Portaled into `.nt` so cards cannot clip it.
 *
 * Turned off in Settings ("Hover hints"), the control renders exactly as it would
 * with no `text` at all — nothing is hidden with CSS, so nothing is left behind to
 * open by keyboard. That setting is deliberately separate from the ⓘ marks: this is
 * help that arrives whether or not it was wanted, and those are help somebody asked
 * for by pointing at a mark. Both start on.
 */
export const HoverTip = ({ heading, text, children, wrapClass }) => {
  const { anchor, box, open, pos, triggers } = useTip(heading, text);
  if (text == null || text === false || text === '' || livePrefs().hoverHints === 'off') return children;
  const raw = Children.count(children) === 1 ? Children.only(children) : <span>{children}</span>;
  const trigger = typeof raw.type === 'string'
    ? cloneElement(raw, {
      ref: anchor,
      onPointerEnter: mergeHandlers(raw.props.onPointerEnter, triggers.onPointerEnter),
      onPointerLeave: mergeHandlers(raw.props.onPointerLeave, triggers.onPointerLeave),
      onPointerDown: mergeHandlers(raw.props.onPointerDown, triggers.onPointerDown),
      onFocus: mergeHandlers(raw.props.onFocus, triggers.onFocus),
      onBlur: mergeHandlers(raw.props.onBlur, triggers.onBlur)
    })
    : (
      <span
        ref={anchor}
        className={wrapClass}
        onPointerEnter={triggers.onPointerEnter}
        onPointerLeave={triggers.onPointerLeave}
        onPointerDown={triggers.onPointerDown}
        onFocus={triggers.onFocus}
        onBlur={triggers.onBlur}
        style={wrapClass ? undefined : { display: 'inline-flex' }}
      >
        {raw}
      </span>
    );
  return (
    <>
      {trigger}
      {open ? <TipPortal box={box} pos={pos} heading={heading}>{text}</TipPortal> : null}
    </>
  );
};

const withTitle = (title, node) => (title ? <HoverTip text={title}>{node}</HoverTip> : node);

export const Card = ({ className, children, ...rest }) => (
  <div className={cx('nt-card', className)} {...rest}>{children}</div>
);

export const CardHead = ({ title, sub, right, children }) => (
  <div className="nt-card-head">
    {title ? <h3>{title}</h3> : null}
    {sub ? <span className="sub">{sub}</span> : null}
    {children}
    {right ? <><span className="nt-spacer" />{right}</> : null}
  </div>
);

export const CardBody = ({ className, children, ...rest }) => (
  <div className={cx('nt-card-body', className)} {...rest}>{children}</div>
);

export const Section = ({ title, dot, right, children }) => (
  <section className="nt-section">
    {(title || right) && (
      <div className="nt-section-head">
        {title ? <h2>{dot ? <span className="nt-dot" style={{ background: dot }} /> : null}{title}</h2> : null}
        <span className="nt-spacer" />
        {right}
      </div>
    )}
    {children}
  </section>
);

export const Button = ({ variant, size, className, children, title, ...rest }) => withTitle(title, (
  <button type="button" className={cx('nt-btn', variant, size, className)} {...rest}>{children}</button>
));

export const IconButton = ({ className, children, title, ...rest }) => withTitle(title, (
  <button type="button" className={cx('nt-icon-btn', className)} {...rest}>{children}</button>
));

export const Badge = ({ tone = 'slate', children, title }) => withTitle(title, (
  <span className={cx('nt-badge', tone)}>{children}</span>
));

export const Chip = ({ on, className, children, title, ...rest }) => withTitle(title, (
  <button type="button" className={cx('nt-chip', on && 'on', className)} {...rest}>{children}</button>
));

export const Note = ({ tone, children, className }) => <div className={cx('nt-note', tone, className)}>{children}</div>;

/**
 * A reading at the top of a view — the shape of the Leads dashboard's data-confidence
 * strip, so every dashboard states its headline the same way: a label and a figure, a
 * bar when the figure is a share, one sentence, and an optional action. `bar` is a list
 * of `{ pct, color }` segments. Plain explanations use Note, which shares the look.
 */
export const Strip = ({ tone = 'slate', eyebrow, value, bar, action, children, className }) => (
  <div className={cx('nt-confidence', tone, className)} role="status">
    {eyebrow || value != null ? (
      <div className="cscore">
        {eyebrow ? <div className="nt-eyebrow">{eyebrow}</div> : null}
        {value != null ? <div className="cnum">{value}</div> : null}
      </div>
    ) : null}
    <div className="cmeter">
      {bar && bar.length ? (
        <div className="cbar" aria-hidden="true">
          {bar.map((seg, i) => (
            <i key={i} style={{ width: `${Math.max(0, Math.min(100, Number(seg.pct) || 0))}%`, background: seg.color }} />
          ))}
        </div>
      ) : null}
      <div className="ctext">{children}</div>
    </div>
    {action ? <div className="cact">{action}</div> : null}
  </div>
);

/**
 * The dates a view covers, written once and the same everywhere: the range in the
 * console's date format, and the period it is compared with when there is one. Plain
 * text on purpose — it states the period, it does not change it, and anything that
 * looks like a picker beside a Period dropdown with Custom reads as a second one.
 * Takes moments or the API's `YYYY-MM-DD` strings.
 */
export const PeriodChip = ({ start, end, compareStart, compareEnd, label, className }) => (
  <span className={cx('nt-period', className)}>
    {label ? <span className="k">{label}</span> : null}
    <b>{formatRange(start, end)}</b>
    {compareStart || compareEnd ? (
      <>
        <span className="vs">vs</span>
        <span className="prev">{formatRange(compareStart, compareEnd)}</span>
      </>
    ) : null}
  </span>
);

/**
 * The circled i. Hover or focus shows the explanation; it is a real button so
 * keyboard and screen-reader users get the same text, not a native `title` that
 * arrives late and vanishes on touch.
 *
 * Portaled into `.nt` so the tooltip is not clipped by a card and still reads the
 * theme tokens. Click is swallowed so an i sitting on a clickable tile does not
 * also open the tile.
 */
export const InfoMark = ({ title, children }) => {
  const { anchor, box, open, pos, show, hide, triggers } = useTip(title, children);
  const label = [title, typeof children === 'string' ? children : null].filter(Boolean).join('. ');

  /* The circled i is the one control whose whole job is to show help, so a tap or a
     click toggles it — that is the only way to read it on a phone. It never sits
     inside a clickable card's action: the event stops here. */
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) hide(); else show();
  };
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  /* ...and the next tap anywhere else puts it away. */
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (anchor.current && anchor.current.contains(e.target)) return;
      if (box.current && box.current.contains(e.target)) return;
      hide();
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [open, anchor, box, hide]);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="nt-imark"
        aria-label={label || 'More information'}
        aria-expanded={open}
        onPointerEnter={triggers.onPointerEnter}
        onPointerLeave={triggers.onPointerLeave}
        onFocus={triggers.onFocus}
        onBlur={triggers.onBlur}
        onClick={toggle}
        onPointerDown={stop}
      >
        <IconInfo />
      </button>
      {open ? <TipPortal box={box} pos={pos} heading={title}>{children}</TipPortal> : null}
    </>
  );
};

/** A figure the API sent — a number, a numeric string, null or "None" — as a number. */
export const num = (v) => Number(v) || 0;

/** Thousands separators, and a dash rather than "0" when there is genuinely nothing. */
export const fmt = (n) => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US'));

/**
 * Tiny 7-point trend, the prototype's sparkline. `data` is real daily values — never
 * a drawn shape. Hover lists now / start / change / high-low the way the prototype
 * does.
 */
export const Spark = ({ data, color, name, suffix = '', note }) => {
  if (!data || data.length < 2) return <span className="nt-spark" aria-hidden="true" />;
  const W = 76;
  const H = 22;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * (W - 4) + 2,
    H - 3 - ((v - min) / span) * (H - 8)
  ]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  const first = data[0];
  const now = data[data.length - 1];
  const chg = first ? Math.round(((now - first) / first) * 100) : 0;
  const show = (v) => `${fmt(v)}${suffix}`;
  return (
    <HoverTip
      heading={name}
      text={(
        <>
          <div className="r"><span>Now</span><b>{show(now)}</b></div>
          <div className="r"><span>7 days ago</span><b>{show(first)}</b></div>
          <div className="r"><span>Change</span><b>{chg > 0 ? '+' : ''}{chg}%</b></div>
          <div className="r"><span>High / low</span><b>{show(max)} / {show(min)}</b></div>
          {note ? <div style={{ marginTop: 5, color: 'var(--nt-muted)' }}>{note}</div> : null}
        </>
      )}
    >
      <span
        className="nt-spark"
        role="img"
        aria-label={`${name}: ${show(first)} to ${show(now)}, ${chg}%`}
      >
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
          <path d={`${d} L${W - 2} ${H} L2 ${H} Z`} fill={color} opacity=".12" />
          <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={last[0]} cy={last[1]} r="2.4" fill={color} />
        </svg>
      </span>
    </HoverTip>
  );
};

export const Kpi = ({ tone = 'slate', value, label, sub, foot, onClick, title, hint, cta, icon, on }) => {
  /* A tile with a call to action or an info mark cannot itself be a <button>: a
     button inside a button is invalid HTML, and the inner one is unreachable by
     keyboard. So those tiles become a div — still clickable with a mouse, with
     the inner control as what keyboard users actually get. */
  const explain = hint || title;
  const Tag = onClick && !cta && !explain ? 'button' : 'div';
  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      className={cx('nt-kpi', tone, onClick && Tag === 'div' && 'clickable', on && 'on')}
      onClick={onClick}
      aria-pressed={onClick && on != null ? !!on : undefined}
    >
      <div className="nt-kpi-top">
        <div>
          <div className="nt-kpi-val">{value}</div>
          <div className="nt-kpi-lab">
            {label}
            {explain ? <InfoMark title={label}>{explain}</InfoMark> : null}
            {sub ? <span className="s">{sub}</span> : null}
          </div>
        </div>
        {icon ? <span className="nt-kpi-ico">{icon}</span> : null}
      </div>
      {foot ? <div className="nt-kpi-foot">{foot}</div> : null}
      {cta ? (
        <button
          type="button"
          className="nt-btn xs nt-kpi-cta"
          onClick={(e) => { e.stopPropagation(); cta.onClick(); }}
        >
          {cta.label}<IconChevR />
        </button>
      ) : null}
    </Tag>
  );
};

/**
 * Period-over-period change.
 *
 * `inverse` is for figures where up is bad — missed calls, did-not-converts. Without
 * it a rise in missed calls renders green, which is worse than showing nothing.
 */
export const Delta = ({ value, cur, prev, inverse, flat }) => {
  /* Every caller writes `cur`, which is what the figure is called on the screens that
     use it. Reading only `value` made the current side zero, so a healthy week showed
     as a 100% fall. Both names work; `cur` wins when both are given. */
  const a = Number(cur != null ? cur : value) || 0;
  const b = Number(prev) || 0;
  /* `flat`: say so when there is nothing to say — "no history" with nothing to compare
     against, "0%" when nothing moved — instead of rendering nothing. The traffic
     screens want that: an empty cell there reads as a figure still loading. */
  if (flat && !b) return <span className="nt-delta flat">no history</span>;
  const diff = a - b;
  if (!diff) return flat ? <span className="nt-delta flat">0%</span> : null;
  const pc = b ? Math.round((diff / b) * 100) : 100;
  const good = inverse ? diff < 0 : diff > 0;
  return <span className={cx('nt-delta', good ? 'up' : 'down')}>{diff > 0 ? '▲' : '▼'} {Math.abs(pc)}%</span>;
};

export const Empty = ({ title, children }) => (
  <div className="nt-empty"><h4>{title}</h4>{children ? <p>{children}</p> : null}</div>
);

/** Matches the height of what it replaces, so a screen does not jump when it loads. */
export const Skeleton = ({ h = 90, className }) => <div className={cx('nt-skel', className)} style={{ height: h }} />;

/**
 * Sorting lives on the header, the way the classic reviewer's TableSortLabel does: a
 * column opts in with `sort` — `true` to sort on its own key, or the key to sort on
 * when the column renders something the key does not name. The page keeps the state
 * and does the comparing; the header only reports the click and shows the arrow.
 *
 * A sortable table keeps its header when there is nothing to show, because a filter
 * that empties it must still be reachable — an unsortable one collapses to the empty
 * state exactly as it always has.
 */
export const Table = ({ columns, rows, empty, minWidth = 0, rowKey, sort, rowClass, onRow, card }) => {
  const sortable = !!(sort && sort.onSort);
  const has = !!(rows && rows.length);
  if (!has && !sortable) {
    return <Empty title={(empty && empty.title) || 'Nothing to show'}>{empty && empty.body}</Empty>;
  }

  const head = (c) => {
    const key = c.sort === true ? c.key : c.sort;
    const name = typeof c.label === 'string' ? c.label : 'this column';
    /* `hint`: the circled i beside the heading — where the value comes from, or what it
       means — as the classic's tables carry. Outside the sort button, since a button
       inside a button is invalid and the sort tip would swallow it. */
    const hint = c.hint ? <InfoMark title={name}>{c.hint}</InfoMark> : null;
    if (!sortable || !key) return <th key={c.key} className={c.align}>{c.label}{hint}</th>;
    const on = sort.by === key;
    const asc = on && sort.dir === 'asc';
    const head = (
      <HoverTip text={on
        ? `Sorted by ${name}, ${asc ? 'smallest first' : 'largest first'} — click to reverse`
        : `Sort by ${name}`}
      >
        <button type="button" className="nt-sort-btn" onClick={() => sort.onSort(key)}>
          <span>{c.label}</span>
          <span className="nt-sort-ind" aria-hidden="true">{on ? (asc ? '▲' : '▼') : '⇅'}</span>
        </button>
      </HoverTip>
    );
    return (
      <th
        key={c.key}
        className={cx(c.align, 'nt-th-sort', on && 'on')}
        aria-sort={on ? (asc ? 'ascending' : 'descending') : 'none'}
      >
        {hint ? <span className="nt-th-hinted">{head}{hint}</span> : head}
      </th>
    );
  };

  /* On a phone the same rows are cards instead.
   *
   * A wide table on a small screen scrolls sideways, which reads as a table with one
   * column and hides everything that made it a table. The classic answers this with a
   * hand-written MobileView per screen — five of them, each drifting from the table it
   * mirrors. Here the card is built from the same `columns` array the table uses, so a
   * column added to one is in both, and a screen cannot ship a mobile view that shows
   * different fields from its desktop one.
   *
   * A column with an empty label is a control rather than a fact — the row's actions —
   * so it goes at the foot of the card without a label instead of as a label/value pair.
   * Both are rendered and CSS picks; a JS breakpoint would flash the wrong one first.
   *
   * `card` overrides that generic shape for a table whose row has a story the label/value
   * stack cannot tell — "this was 120, now it is 30" reads as a sentence, not as four
   * separate facts. It still draws from these same rows, so the two views cannot show
   * different data; only the arrangement differs.
   */
  const cards = (
    <div className="nt-cards">
      {rows.map((r, i) => {
        const key = rowKey ? rowKey(r, i) : (r.id != null ? r.id : i);
        if (card) return <Fragment key={key}>{card(r, i)}</Fragment>;
        const facts = columns.filter((c) => c.label && (c.render ? c.render(r) : r[c.key]) != null);
        const controls = columns.filter((c) => !c.label);
        return (
          <article className="nt-card-row" key={key}>
            <dl>
              {facts.map((c) => (
                <div key={c.key}>
                  <dt>{c.label}</dt>
                  <dd>{c.render ? c.render(r) : r[c.key]}</dd>
                </div>
              ))}
            </dl>
            {controls.length ? (
              <div className="nt-card-row-foot">
                {controls.map((c) => (
                  <span key={c.key}>{c.render ? c.render(r) : r[c.key]}</span>
                ))}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="nt-table-wrap">
      <table className="nt-tbl" style={minWidth ? { minWidth } : undefined}>
        <thead><tr>{columns.map(head)}</tr></thead>
        <tbody>
          {has ? rows.map((r, i) => (
            <tr
              key={rowKey ? rowKey(r, i) : (r.id != null ? r.id : i)}
              className={cx(rowClass && rowClass(r), onRow && 'nt-row-go')}
              onClick={onRow ? (e) => {
                if (e.target.closest('a, button, input, select, textarea, [role="button"]')) return;
                onRow(r);
              } : undefined}
            >
              {columns.map((c) => <td key={c.key} className={c.align}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          )) : (
            <tr className="nt-tbl-empty">
              <td colSpan={columns.length}>
                <Empty title={(empty && empty.title) || 'Nothing to show'}>{empty && empty.body}</Empty>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      {has ? cards : (
        <div className="nt-cards">
          <Empty title={(empty && empty.title) || 'Nothing to show'}>{empty && empty.body}</Empty>
        </div>
      )}
    </>
  );
};

/**
 * Clicking the column already sorted flips it; a new column starts in the direction that
 * suits it — text reads A-Z, numbers read largest first. Name the text columns in
 * `ascFirst`.
 *
 * The second half of that is a deliberate departure, and this comment used to claim the
 * opposite. The classic starts EVERY new column descending, which on a name column means
 * the first click sorts Z-A — nobody wants that, and it costs a second click every time.
 * Numbers keep the classic's behaviour, because largest-first is what a cost or a count
 * is being sorted for.
 */
export const useSort = (initialBy, initialDir = 'desc', ascFirst = []) => {
  const [by, setBy] = useState(initialBy);
  const [dir, setDir] = useState(initialDir);
  const onSort = (key) => {
    if (key === by) { setDir(dir === 'asc' ? 'desc' : 'asc'); return; }
    setBy(key);
    setDir(ascFirst.indexOf(key) >= 0 ? 'asc' : 'desc');
  };
  return { by, dir, onSort, setBy, setDir };
};

/**
 * Sorts rows by the current column. Strings compare as text, everything else as
 * numbers with a missing value treated as -1 — the same comparison the classic
 * reviewer makes, so the two consoles order an identical list identically. Pass
 * `accessors` when a column's value is not simply `row[key]`.
 */
export const sortRows = (rows, sort, accessors = {}) => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const get = accessors[sort.by] || ((r) => (r ? r[sort.by] : undefined));
  return rows.slice().sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (typeof av === 'string' || typeof bv === 'string') {
      return dir * String(av == null ? '' : av).localeCompare(String(bv == null ? '' : bv));
    }
    const an = av == null ? -1 : Number(av) || 0;
    const bn = bv == null ? -1 : Number(bv) || 0;
    return dir * (an - bn);
  });
};


/** "Bellevue Chiropractic" → "BC"; `fallback` for an empty name. */
export const initialsOf = (name, fallback = '?') => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
};
export const pct = (a, b) => (!b ? 0 : Math.round((Number(a) / Number(b)) * 100));

/**
 * The pager under a table, with the page-size control the classic has.
 *
 * Fixing the page size at 20 quietly removed a choice: on a portfolio of 28 clinics the
 * difference between 20 and 50 rows is one page or two, and somebody scanning for the
 * clinic in trouble was being made to paginate for no reason. The options are the
 * classic's own — 20, 30, 50.
 *
 * Page is 1-based here and in every endpoint behind it. Changing the size resets to the
 * first page: page 4 of a 20-row list is not page 4 of a 50-row one, and keeping the
 * number lands the reader somewhere they did not ask to be — or past the end.
 */
const PAGE_SIZES = [20, 30, 50];

/* True at phone width, and it follows rotation and resizing. The query is the one the
   phone stylesheet uses, so a screen's code and its CSS agree about what a phone is. */
const PHONE_QUERY = '(max-width: 640px)';
export const usePhone = () => {
  const [phone, setPhone] = useState(() => (
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches
  ));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const sync = () => setPhone(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync); else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync); else mq.removeListener(sync);
    };
  }, []);
  return phone;
};

/**
 * For a screen whose list grows on a phone: returns whether this is a phone, and puts
 * the list back to page 1 whenever that changes. A phone shows pages 1..n appended, a
 * wide screen shows page n alone, so neither can be carried into the other — a desktop
 * on page 3 turned into a phone list starting at page 3, and a phone that had scrolled
 * through four pages turned into a desktop "page 4" holding eighty rows. On a phone the
 * list also starts at page 1, whatever page a shared link asked for.
 */
export const usePhonePaging = (setPage) => {
  const phone = usePhone();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      if (phone) setPage(1);
      return;
    }
    setPage(1);
  }, [phone, setPage]);
  return phone;
};

/**
 * Page controls — and, with `scroll`, the classic's mobile behaviour: on a phone the
 * next page loads when the end of the list scrolls into view, instead of Prev and Next.
 *
 * `scroll` is opt-in on purpose. The trigger only advances `page`; the screen has to
 * append that page to what is already shown. A screen that still replaces its rows
 * would, on a phone, step through every page by itself while the end of the list sat
 * on screen. Screens pass `scroll` once they append, and `loadingMore` while a later
 * page is on its way.
 *
 * The trigger is the classic's own hook (hooks/useInfiniteScroll) — the same "fully in
 * view" threshold and the same guard against firing while a page loads. Both controls
 * are rendered and CSS chooses, so a wide screen never sees the strip and a phone
 * never sees the buttons. "Load more" stays as a fallback for anyone who would rather
 * tap than scroll.
 */
export const Pager = ({
  page, pages, total, size, onPage, onSize, noun = 'in total',
  sizes, sizeLabel = 'Rows', rangeNoun, className,
  scroll = false, loadingMore = false
}) => {
  const from = total ? (page - 1) * Number(size) + 1 : 0;
  const to = Math.min(page * Number(size), total);
  const info = rangeNoun
    ? `${rangeNoun} ${from}–${to} of ${fmt(total)}`
    : `Page ${page} of ${pages} · ${fmt(total)} ${noun}`;
  const hasMore = scroll && page < pages;
  const loadNext = useCallback(() => onPage(page + 1), [onPage, page]);
  const { targetRef } = useInfiniteScroll({
    disabled: !hasMore || loadingMore,
    isLoading: loadingMore,
    loadMore: loadNext
  });
  return (
    <>
    <div className={cx('nt-pager', scroll && 'nt-pager-desk', className)}>
      <span className="info">{info}</span>
      {onSize ? (
        <label className="nt-pager-size">
          <span>{sizeLabel}</span>
          <select
            className="nt-select"
            value={size}
            onChange={(e) => onSize(Number(e.target.value))}
            aria-label={sizeLabel}
          >
            {(sizes || PAGE_SIZES).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      ) : null}
      <span className="nt-spacer" />
      <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}><IconChevL />Prev</Button>
      <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next<IconChevR /></Button>
    </div>
    {scroll ? (
      /* The end of the list on a phone, the way a mobile app does it: a spinner while the
         next page is on its way, "No more records" once the list has actually been
         scrolled to its end, and otherwise nothing at all — no page numbers, no counts,
         no button. A list that fits on its first page ends without a footer. The empty
         strip is still there while more remains, because it is what the scroll watches. */
      <div
        className={cx('nt-pager-more', !loadingMore && !(page > 1 && !hasMore) && 'quiet')}
        ref={targetRef}
        aria-live="polite"
      >
        {loadingMore ? <span className="nt-spin-ring" role="status" aria-label="Loading more" /> : null}
        {!loadingMore && page > 1 && !hasMore ? <span className="end">No more records</span> : null}
      </div>
    ) : null}
    </>
  );
};
